import { FormEvent, useCallback, useEffect, useMemo, useState } from "react";
import * as adminUserService from "../services/adminUserService";
import * as simulatedTradeService from "../services/simulatedTradeService";
import type { Order, TradingPosition } from "../types/api";
import type { SimulationClientSummary } from "../services/adminUserService";
import type {
  ClientLimitSummary,
  SimulationOpenOrder,
} from "../services/simulatedTradeService";
import { Input } from "../components/ui/input";
import { Button } from "../components/ui/button";
import { Label } from "../components/ui/label";
import { useToast } from "../components/ui/use-toast";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import { useAuth } from "../hooks/useAuth";
import { ClientBotsModal } from "../components/admin/ClientBotsModal";
import { cn } from "../lib/utils";

const parseNumericInput = (value: string) =>
  Number.parseFloat(value.replace(/,/g, ".").trim());

/** Champs formulaire admin : lisibles en mode clair et sombre */
const fieldClass =
  "bg-background text-foreground placeholder:text-muted-foreground border border-input dark:bg-white/5 dark:text-white dark:placeholder:text-white/50";
const fieldDisabledClass =
  "bg-muted text-muted-foreground placeholder:text-muted-foreground border border-input dark:bg-white/5 dark:text-white/50 dark:placeholder:text-white/50";
const selectTriggerClass =
  "bg-background text-foreground border-input dark:bg-white/5 dark:text-white";

const AdminSimulatedTradesPage = () => {
  const { toast } = useToast();
  const { user } = useAuth();
  const [clients, setClients] = useState<SimulationClientSummary[]>([]);
  const [selectedClient, setSelectedClient] =
    useState<SimulationClientSummary | null>(null);
  const [clientPositions, setClientPositions] = useState<TradingPosition[]>([]);
  const [clientLimitSummaries, setClientLimitSummaries] = useState<
    ClientLimitSummary[]
  >([]);
  const [clientOpenOrders, setClientOpenOrders] = useState<
    SimulationOpenOrder[]
  >([]);
  const [isLoadingClients, setIsLoadingClients] = useState(true);
  const [isLoadingPositions, setIsLoadingPositions] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [recentTrades, setRecentTrades] = useState<Order[]>([]);
  const [selectedOrderId, setSelectedOrderId] = useState<number | null>(null);
  const [botsModalOpen, setBotsModalOpen] = useState(false);

  const [form, setForm] = useState({
    symbol: "",
    side: "BUY" as "BUY" | "SELL",
    quantity: "",
    // Option 1: Prix d'exécution
    executedPrice: "",
    // Option STOP : prix de déclenchement
    stopTriggerPrice: "",
    // Option 2: Gain/perte direct
    profitLossAmount: "",
    useProfitLoss: false, // Toggle entre prix d'exécution et gain/perte
  });

  const fetchClients = useCallback(async () => {
    setIsLoadingClients(true);
    try {
      const data = await adminUserService.listSimulationClients();
      setClients(data);
    } catch (err) {
      toast({
        title: "Erreur",
        description:
          (err as Error)?.message ?? "Impossible de charger les clients.",
        variant: "destructive",
      });
    } finally {
      setIsLoadingClients(false);
    }
  }, [toast]);

  const fetchClientPositions = useCallback(async (userId: number) => {
    setIsLoadingPositions(true);
    try {
      const positions = await adminUserService.getUserPositions(userId);
      setClientPositions(positions);
    } catch (err) {
      console.error("Erreur lors du chargement des positions:", err);
      setClientPositions([]);
    } finally {
      setIsLoadingPositions(false);
    }
  }, []);

  const fetchClientLimitSummary = useCallback(async (userId: number) => {
    try {
      const summary = await simulatedTradeService.listClientLimitSummary(
        userId
      );
      setClientLimitSummaries(summary);
    } catch (err) {
      console.error(
        "Erreur lors du chargement des ordres LIMIT du client:",
        err
      );
      setClientLimitSummaries([]);
    }
  }, []);

  const fetchClientOpenOrders = useCallback(async (userId: number) => {
    try {
      const orders =
        await simulatedTradeService.listClientOpenOrdersForSimulation(userId);
      setClientOpenOrders(orders);
    } catch (err) {
      console.error(
        "Erreur lors du chargement des ordres ouverts du client:",
        err
      );
      setClientOpenOrders([]);
    }
  }, []);

  useEffect(() => {
    void fetchClients();
  }, [fetchClients]);

  // Charger les positions quand un client est sélectionné
  useEffect(() => {
    if (selectedClient) {
      void fetchClientPositions(selectedClient.id);
      void fetchClientLimitSummary(selectedClient.id);
      void fetchClientOpenOrders(selectedClient.id);
      setSelectedOrderId(null);
    } else {
      setClientPositions([]);
      setClientLimitSummaries([]);
      setClientOpenOrders([]);
      setSelectedOrderId(null);
      setForm((prev) => ({
        ...prev,
        symbol: "",
        side: "BUY",
        quantity: "",
      }));
    }
  }, [
    selectedClient,
    fetchClientPositions,
    fetchClientLimitSummary,
    fetchClientOpenOrders,
  ]);

  const getMaxSimulatableQuantity = useCallback(
    (symbol: string, side: "BUY" | "SELL"): number => {
      const selectedOrder = clientOpenOrders.find(
        (order) => order.id === selectedOrderId
      );
      if (selectedOrder) {
        return selectedOrder.remainingQuantity ?? 0;
      }
      if (!symbol) return 0;
      const oppositeSide = side === "BUY" ? "SELL" : "BUY";
      return clientLimitSummaries
        .filter(
          (s) =>
            s.symbol === symbol && s.side === (oppositeSide as "BUY" | "SELL")
        )
        .reduce((acc, s) => acc + (s.remainingQuantity ?? 0), 0);
    },
    [clientLimitSummaries, clientOpenOrders, selectedOrderId]
  );

  const handleFormChange = (
    event: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>
  ) => {
    const { name, value, type } = event.target;
    if (type === "checkbox") {
      const checked = (event.target as HTMLInputElement).checked;
      setForm((prev) => ({ ...prev, [name]: checked }));
      return;
    }

    // Gestion spécifique de la quantité pour respecter les limites des ordres LIMIT
    if (name === "quantity") {
      const raw = value;
      let numeric = Number.parseFloat(raw.replace(/,/g, ".").trim());
      if (Number.isNaN(numeric) || numeric < 0) {
        numeric = 0;
      }
      const max = getMaxSimulatableQuantity(form.symbol, form.side);
      if (max > 0 && numeric > max) {
        numeric = max;
      }
      setForm((prev) => ({
        ...prev,
        quantity: numeric ? String(numeric) : "",
      }));
      return;
    }

    setForm((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (event: FormEvent) => {
    event.preventDefault();

    if (!selectedClient) {
      toast({
        title: "Erreur",
        description: "Veuillez sélectionner un client",
        variant: "destructive",
      });
      return;
    }

    const selectedOrder = selectedOrderId
      ? clientOpenOrders.find((order) => order.id === selectedOrderId)
      : null;

    // Valider le symbole (ne doit pas être "__custom__" sans valeur saisie)
    const symbolToUse = selectedOrder
      ? selectedOrder.symbol
      : form.symbol === "__custom__"
      ? ""
      : form.symbol.toUpperCase();
    if (!symbolToUse || !form.quantity) {
      toast({
        title: "Erreur",
        description: "Veuillez remplir tous les champs obligatoires",
        variant: "destructive",
      });
      return;
    }

    if (selectedOrder) {
      const expectedSide = selectedOrder.side === "BUY" ? "SELL" : "BUY";
      if (form.side !== expectedSide) {
        toast({
          title: "Erreur",
          description: `Pour exécuter cet ordre ${selectedOrder.side}, le type d'opération doit être ${expectedSide}.`,
          variant: "destructive",
        });
        return;
      }
    }

    // Vérifier qu'il existe bien des ordres LIMIT/STOP LIMIT du côté opposé pour cette paire
    const maxForSide = getMaxSimulatableQuantity(
      symbolToUse,
      form.side as "BUY" | "SELL"
    );
    if (!maxForSide || maxForSide <= 0) {
      toast({
        title: "Impossible de simuler ce trade",
        description:
          form.side === "BUY"
            ? "Le client n'a aucun ordre LIMIT/STOP LIMIT SELL ouvert sur cette paire."
            : "Le client n'a aucun ordre LIMIT/STOP LIMIT BUY ouvert sur cette paire.",
        variant: "destructive",
      });
      return;
    }

    if (!form.useProfitLoss && !form.executedPrice) {
      toast({
        title: "Erreur",
        description: "Veuillez fournir un prix d'exécution ou un gain/perte",
        variant: "destructive",
      });
      return;
    }

    // Validation du prix d'exécution par rapport aux ordres LIMIT du client
    let executedPriceNumber: number | null = null;
    let stopTriggerNumber: number | null = null;
    if (!form.useProfitLoss) {
      executedPriceNumber = Number.parseFloat(
        String(form.executedPrice).replace(/,/g, ".").trim()
      );
      if (!Number.isFinite(executedPriceNumber) || executedPriceNumber <= 0) {
        toast({
          title: "Erreur",
          description: "Veuillez fournir un prix d'exécution valide",
          variant: "destructive",
        });
        return;
      }

      if (selectedOrder) {
        const price = Number(selectedOrder.price ?? 0);
        const stopPrice = Number(selectedOrder.stopPrice ?? 0);
        if (selectedOrder.type === "STOP_LIMIT") {
          stopTriggerNumber = Number.parseFloat(
            String(form.stopTriggerPrice).replace(/,/g, ".").trim()
          );
          if (!Number.isFinite(stopTriggerNumber) || stopTriggerNumber <= 0) {
            toast({
              title: "Erreur",
              description: "Veuillez fournir un prix de déclenchement valide.",
              variant: "destructive",
            });
            return;
          }
          const stopTriggered =
            selectedOrder.side === "BUY"
              ? stopTriggerNumber >= stopPrice
              : stopTriggerNumber <= stopPrice;
          if (!stopTriggered) {
            toast({
              title: "Stop non déclenché",
              description:
                "Le prix de déclenchement ne respecte pas le stop de l'ordre sélectionné.",
              variant: "destructive",
            });
            return;
          }
        }
        const limitOk =
          selectedOrder.side === "BUY"
            ? executedPriceNumber <= price
            : executedPriceNumber >= price;
        if (!limitOk) {
          toast({
            title: "Prix non conforme",
            description:
              "Le prix d'exécution ne respecte pas la limite de l'ordre sélectionné.",
            variant: "destructive",
          });
          return;
        }
      } else {
        const oppositeSide = form.side === "BUY" ? "SELL" : "BUY";
        const limitSummary = clientLimitSummaries.find(
          (s) =>
            s.symbol === symbolToUse &&
            s.side === (oppositeSide as "BUY" | "SELL") &&
            (s.remainingQuantity ?? 0) > 0
        );

        if (limitSummary) {
          const { minPrice, maxPrice } = limitSummary;
          if (
            form.side === "BUY" &&
            typeof minPrice === "number" &&
            executedPriceNumber < minPrice
          ) {
            toast({
              title: "Prix trop bas",
              description: `Pour exécuter les ordres LIMIT SELL du client, le prix doit être au moins ${minPrice.toFixed(
                2
              )}.`,
              variant: "destructive",
            });
            return;
          }
          if (
            form.side === "SELL" &&
            typeof maxPrice === "number" &&
            executedPriceNumber > maxPrice
          ) {
            toast({
              title: "Prix trop élevé",
              description: `Pour exécuter les ordres LIMIT BUY du client, le prix doit être au plus ${maxPrice.toFixed(
                2
              )}.`,
              variant: "destructive",
            });
            return;
          }
        }
      }
    }

    const parsedProfitLoss = form.useProfitLoss
      ? parseNumericInput(form.profitLossAmount)
      : null;

    if (
      form.useProfitLoss &&
      (parsedProfitLoss === null || Number.isNaN(parsedProfitLoss))
    ) {
      toast({
        title: "Erreur",
        description: "Veuillez fournir un montant de gain/perte valide",
        variant: "destructive",
      });
      return;
    }

    setIsSubmitting(true);
    try {
      const payload: simulatedTradeService.SimulateTradePayload = {
        userId: selectedClient.id,
        symbol: symbolToUse,
        side: form.side,
        quantity: Number.parseFloat(form.quantity),
      };
      if (selectedOrder) {
        payload.orderId = selectedOrder.id;
      }

      if (form.useProfitLoss) {
        payload.profitLossAmount = parsedProfitLoss ?? undefined;
      } else {
        payload.executedPrice = Number.parseFloat(form.executedPrice);
      }
      if (!form.useProfitLoss && selectedOrder?.type === "STOP_LIMIT") {
        payload.stopTriggerPrice = Number.parseFloat(form.stopTriggerPrice);
      }

      const order = await simulatedTradeService.simulateTrade(payload);

      toast({
        title: "Succès",
        description: `Trade simulé créé avec succès pour ${selectedClient.firstName} ${selectedClient.lastName}`,
      });

      // Réinitialiser le formulaire
      setForm({
        symbol: "",
        side: "BUY",
        quantity: "",
        executedPrice: "",
        stopTriggerPrice: "",
        profitLossAmount: "",
        useProfitLoss: false,
      });
      setSelectedOrderId(null);

      // Ajouter le trade à la liste récente
      setRecentTrades((prev) => [order, ...prev].slice(0, 10));
    } catch (err) {
      const message =
        typeof err === "string"
          ? err
          : err instanceof Error
          ? err.message
          : "Impossible de créer le trade simulé.";
      toast({
        title: "Erreur",
        description: message,
        variant: "destructive",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  // Vérifier si l'utilisateur a la permission
  const hasPermission =
    user?.permissions?.includes("trades.simulate") ||
    user?.role === "admin" ||
    user?.role === "manager_trader";

  if (!hasPermission) {
    return (
      <div className="rounded-3xl border border-border bg-muted/30 p-8 text-center text-foreground dark:border-white/10 dark:bg-white/5 dark:text-white">
        <h2 className="text-xl font-semibold">Accès refusé</h2>
        <p className="mt-2 text-muted-foreground dark:text-white/70">
          Vous n'avez pas la permission de simuler des trades.
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="rounded-3xl border border-border bg-muted/30 p-6 dark:border-white/10 dark:bg-white/5">
        <h1 className="mb-6 text-2xl font-semibold text-foreground dark:text-white">
          Simuler un Trade
        </h1>
        <p className="mb-6 text-sm text-muted-foreground dark:text-white/70">
          Créez un trade simulé pour un client. Les trades simulés mettent à
          jour le portefeuille du client sans toucher à ses comptes trading.
        </p>

        <form onSubmit={handleSubmit} className="space-y-4">
          {/* Sélection du client */}
          <div className="space-y-2">
            <Label htmlFor="client" className="text-foreground dark:text-white/90">
              Client *
            </Label>
            <div className="flex flex-wrap items-center gap-2">
              <Select
                value={selectedClient?.id.toString() ?? ""}
                onValueChange={(value) => {
                  const client = clients.find((c) => c.id === Number(value));
                  setSelectedClient(client ?? null);
                }}>
                <SelectTrigger
                  className={cn(selectTriggerClass, "w-full min-w-[200px]")}>
                  <SelectValue placeholder="Sélectionner un client" />
                </SelectTrigger>
                <SelectContent>
                  {isLoadingClients ? (
                    <SelectItem value="loading" disabled>
                      Chargement...
                    </SelectItem>
                  ) : clients.length === 0 ? (
                    <SelectItem value="empty" disabled>
                      Aucun client disponible
                    </SelectItem>
                  ) : (
                    clients.map((client) => (
                      <SelectItem
                        key={client.id}
                        value={client.id.toString()}>
                        {client.firstName} {client.lastName} ({client.email})
                      </SelectItem>
                    ))
                  )}
                </SelectContent>
              </Select>
              {selectedClient && (
                <Button
                  type="button"
                  variant="outline"
                  className="shrink-0"
                  onClick={() => setBotsModalOpen(true)}>
                  Robots
                </Button>
              )}
            </div>
          </div>
          <ClientBotsModal
            client={
              selectedClient
                ? {
                    id: selectedClient.id,
                    firstName: selectedClient.firstName,
                    lastName: selectedClient.lastName,
                  }
                : null
            }
            open={botsModalOpen}
            onOpenChange={setBotsModalOpen}
          />
          {/* Symbole - Select basé sur les positions du client */}
          <div className="space-y-2">
            <Label htmlFor="symbol" className="text-foreground dark:text-white/90">
              Paire de devises *
            </Label>
            {!selectedClient ? (
              <Input
                id="symbol"
                name="symbol"
                type="text"
                placeholder="Sélectionnez d'abord un client"
                value=""
                disabled
                className={fieldDisabledClass}
              />
            ) : isLoadingPositions &&
              clientPositions.length === 0 &&
              clientOpenOrders.length === 0 ? (
              <Input
                id="symbol"
                name="symbol"
                type="text"
                placeholder="Chargement des positions..."
                value=""
                disabled
                className={fieldDisabledClass}
              />
            ) : clientPositions.length === 0 &&
              clientOpenOrders.length === 0 ? (
              <Input
                id="symbol"
                name="symbol"
                type="text"
                placeholder="BTC/USD, ETH/USD, etc. (le client n'a pas de positions)"
                value={form.symbol}
                onChange={handleFormChange}
                className={fieldClass}
                required
              />
            ) : (
              <>
                <Select
                  value={
                    selectedOrderId
                      ? `order:${selectedOrderId}`
                      : form.symbol === "__unavailable__"
                      ? ""
                      : form.symbol === "__custom__"
                      ? "__custom__"
                      : form.symbol
                  }
                  onValueChange={(value) => {
                    if (value === "__custom__") {
                      setSelectedOrderId(null);
                      setForm((prev) => ({
                        ...prev,
                        symbol: "__custom__",
                        quantity: "",
                      }));
                      return;
                    }

                    if (value.startsWith("order:")) {
                      const orderId = Number(value.replace("order:", ""));
                      const selectedOrder = clientOpenOrders.find(
                        (order) => order.id === orderId
                      );
                      if (!selectedOrder) {
                        return;
                      }
                      const expectedSide =
                        selectedOrder.side === "BUY" ? "SELL" : "BUY";
                      setSelectedOrderId(orderId);
                      const defaultExecutedPrice =
                        selectedOrder.price ?? selectedOrder.stopPrice ?? null;
                      setForm((prev) => ({
                        ...prev,
                        symbol: selectedOrder.symbol,
                        side: expectedSide,
                        quantity: String(selectedOrder.remainingQuantity ?? 0),
                        executedPrice:
                          defaultExecutedPrice !== null
                            ? String(defaultExecutedPrice)
                            : prev.executedPrice,
                        stopTriggerPrice:
                          selectedOrder.type === "STOP_LIMIT" &&
                          selectedOrder.stopPrice !== null
                            ? String(selectedOrder.stopPrice)
                            : prev.stopTriggerPrice,
                      }));
                      return;
                    }

                    setSelectedOrderId(null);
                    // Sélection d'une paire à partir des positions autorisées pour ce type d'opération
                    setForm((prev) => {
                      const nextSide = prev.side;
                      const maxQty = getMaxSimulatableQuantity(value, nextSide);
                      return {
                        ...prev,
                        symbol: value,
                        quantity:
                          maxQty > 0 ? String(maxQty) : prev.quantity ?? "",
                      };
                    });
                  }}>
                  <SelectTrigger className={selectTriggerClass}>
                    <SelectValue placeholder="Sélectionner une paire" />
                  </SelectTrigger>
                  <SelectContent>
                    {(() => {
                      const openOrdersAvailable = clientOpenOrders.filter(
                        (order) => (order.remainingQuantity ?? 0) > 0
                      );

                      if (openOrdersAvailable.length > 0) {
                        return openOrdersAvailable.map((order) => {
                          const priceLabel =
                            order.type === "STOP_LIMIT"
                              ? `stop ${Number(order.stopPrice ?? 0).toFixed(
                                  2
                                )} / limit ${Number(order.price ?? 0).toFixed(
                                  2
                                )}`
                              : `limit ${Number(order.price ?? 0).toFixed(2)}`;
                          const expectedSide =
                            order.side === "BUY" ? "SELL" : "BUY";
                          return (
                            <SelectItem
                              key={order.id}
                              value={`order:${order.id}`}>
                              {order.symbol} • {order.type} {order.side} • reste{" "}
                              {Number(order.remainingQuantity ?? 0).toFixed(8)}{" "}
                              • {priceLabel} • simul: {expectedSide}
                            </SelectItem>
                          );
                        });
                      }

                      const oppositeSide = form.side === "BUY" ? "SELL" : "BUY";
                      const allowedSymbols = new Set(
                        clientLimitSummaries
                          .filter(
                            (s) =>
                              s.side === oppositeSide &&
                              (s.remainingQuantity ?? 0) > 0
                          )
                          .map((s) => s.symbol)
                      );
                      const filteredPositions =
                        allowedSymbols.size > 0
                          ? clientPositions.filter((position) =>
                              allowedSymbols.has(position.marketSymbol)
                            )
                          : clientPositions;

                      if (filteredPositions.length === 0) {
                        return (
                          <SelectItem
                            value="__unavailable__"
                            disabled
                            className="text-muted-foreground">
                            Aucun ordre LIMIT/STOP LIMIT pour ce type
                            d'opération
                          </SelectItem>
                        );
                      }

                      return filteredPositions.map((position) => (
                        <SelectItem
                          key={position.marketSymbol}
                          value={position.marketSymbol}>
                          {position.marketSymbol} (Quantité:{" "}
                          {position.quantity.toFixed(8)})
                        </SelectItem>
                      ));
                    })()}
                    <SelectItem value="__custom__">
                      Autre (saisie manuelle)
                    </SelectItem>
                  </SelectContent>
                </Select>
                {form.symbol === "__custom__" && (
                  <Input
                    id="symbol-custom"
                    name="symbol"
                    type="text"
                    placeholder="BTC/USD, ETH/USD, etc."
                    value={form.symbol === "__custom__" ? "" : form.symbol}
                    onChange={(e) =>
                      setForm((prev) => ({ ...prev, symbol: e.target.value }))
                    }
                    className={cn("mt-2", fieldClass)}
                    required
                  />
                )}
                {selectedOrderId &&
                  (() => {
                    const selected = clientOpenOrders.find(
                      (order) => order.id === selectedOrderId
                    );
                    if (!selected) {
                      return null;
                    }
                    const limitText =
                      selected.price !== null && Number.isFinite(selected.price)
                        ? Number(selected.price).toFixed(2)
                        : "--";
                    const stopText =
                      selected.stopPrice !== null &&
                      Number.isFinite(selected.stopPrice)
                        ? Number(selected.stopPrice).toFixed(2)
                        : "--";
                    return (
                      <div className="mt-2 rounded-2xl border border-cyan-500/30 bg-cyan-50 p-3 text-xs text-cyan-950 dark:border-cyan-500/20 dark:bg-cyan-500/10 dark:text-cyan-100">
                        Ordre sélectionné : {selected.symbol} • {selected.type}{" "}
                        {selected.side} • reste{" "}
                        {Number(selected.remainingQuantity ?? 0).toFixed(8)} •
                        stop {stopText} / limite {limitText}
                      </div>
                    );
                  })()}
              </>
            )}
          </div>

          {/* Côté (BUY/SELL) */}
          <div className="space-y-2">
            <Label htmlFor="side" className="text-foreground dark:text-white/90">
              Type d'opération *
            </Label>
            <Select
              value={form.side}
              disabled={Boolean(selectedOrderId)}
              onValueChange={(value) => {
                const nextSide = value as "BUY" | "SELL";
                setSelectedOrderId(null);
                setForm((prev) => {
                  const max = getMaxSimulatableQuantity(prev.symbol, nextSide);
                  return {
                    ...prev,
                    side: nextSide,
                    quantity: max > 0 ? String(max) : prev.quantity, // ne change pas si pas de limites
                  };
                });
              }}>
              <SelectTrigger className={selectTriggerClass}>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="BUY">
                  Achat (BUY)
                </SelectItem>
                <SelectItem value="SELL">
                  Vente (SELL)
                </SelectItem>
              </SelectContent>
            </Select>
          </div>

          {/* Quantité */}
          <div className="space-y-2">
            <Label htmlFor="quantity" className="text-foreground dark:text-white/90">
              Quantité *
            </Label>
            <Input
              id="quantity"
              name="quantity"
              type="number"
              step="0.00000001"
              min="0"
              placeholder="0.00000000"
              value={form.quantity}
              onChange={handleFormChange}
              className={fieldClass}
              required
            />
          </div>

          {/* Toggle entre prix d'exécution et gain/perte */}
          <div className="flex items-center space-x-2">
            <input
              type="checkbox"
              id="useProfitLoss"
              name="useProfitLoss"
              checked={form.useProfitLoss}
              onChange={handleFormChange}
              className="h-4 w-4 rounded border-input bg-background text-primary focus:ring-primary dark:border-white/20 dark:bg-white/5 dark:text-cyan-400 dark:focus:ring-cyan-400"
            />
            <Label htmlFor="useProfitLoss" className="text-foreground dark:text-white/90">
              Utiliser un gain/perte direct (ex: +10, -5, +500)
            </Label>
          </div>

          {/* Prix d'exécution ou Gain/perte */}
          {form.useProfitLoss ? (
            <div className="space-y-2">
              <Label htmlFor="profitLossAmount" className="text-foreground dark:text-white/90">
                Gain/Perte (montant total) *
              </Label>
              <Input
                id="profitLossAmount"
                name="profitLossAmount"
                type="text"
                inputMode="decimal"
                placeholder="+10, -5, +500 (montant total du gain/perte)"
                value={form.profitLossAmount}
                onChange={handleFormChange}
                className={fieldClass}
                required={form.useProfitLoss}
              />
              <p className="text-xs text-muted-foreground dark:text-white/60">
                Exemple: +10 pour un gain de 10$, -5 pour une perte de 5$
              </p>
            </div>
          ) : (
            <div className="space-y-2">
              <Label htmlFor="executedPrice" className="text-foreground dark:text-white/90">
                Prix d'exécution *
              </Label>
              <Input
                id="executedPrice"
                name="executedPrice"
                type="number"
                step="0.01"
                min="0"
                placeholder="3500.00"
                value={form.executedPrice}
                onChange={handleFormChange}
                className={fieldClass}
                required={!form.useProfitLoss}
              />
              {selectedOrderId &&
                (() => {
                  const selected = clientOpenOrders.find(
                    (order) => order.id === selectedOrderId
                  );
                  if (!selected || selected.type !== "STOP_LIMIT") {
                    return null;
                  }
                  return (
                    <div className="space-y-2">
                      <Label
                        htmlFor="stopTriggerPrice"
                        className="text-foreground dark:text-white/90">
                        Prix de déclenchement (stop) *
                      </Label>
                      <Input
                        id="stopTriggerPrice"
                        name="stopTriggerPrice"
                        type="number"
                        step="0.01"
                        min="0"
                        placeholder="0.00"
                        value={form.stopTriggerPrice}
                        onChange={handleFormChange}
                        className={fieldClass}
                        required
                      />
                      <p className="text-xs text-muted-foreground dark:text-white/60">
                        Exemple : stop{" "}
                        {Number(selected.stopPrice ?? 0).toFixed(2)} puis
                        exécution à {Number(selected.price ?? 0).toFixed(2)}.
                      </p>
                    </div>
                  );
                })()}
              {selectedOrderId &&
                (() => {
                  const selected = clientOpenOrders.find(
                    (order) => order.id === selectedOrderId
                  );
                  if (!selected) {
                    return null;
                  }
                  const stopText =
                    selected.stopPrice !== null &&
                    Number.isFinite(selected.stopPrice)
                      ? Number(selected.stopPrice).toFixed(2)
                      : "--";
                  const limitText =
                    selected.price !== null && Number.isFinite(selected.price)
                      ? Number(selected.price).toFixed(2)
                      : "--";
                  return (
                    <p className="text-xs text-muted-foreground dark:text-white/60">
                      Stop: {stopText} • Limite: {limitText}. Ces valeurs
                      viennent de l'ordre client et ne sont pas modifiables ici.
                      Modifie l'ordre côté client si besoin.
                    </p>
                  );
                })()}
            </div>
          )}

          <Button
            type="submit"
            disabled={isSubmitting}
            className="w-full bg-cyan-500 text-white hover:bg-cyan-600">
            {isSubmitting ? "Création..." : "Créer le trade simulé"}
          </Button>
        </form>
      </div>

      {/* Historique récent */}
      {recentTrades.length > 0 && (
        <div className="rounded-3xl border border-border bg-muted/30 p-6 dark:border-white/10 dark:bg-white/5">
          <h2 className="mb-4 text-xl font-semibold text-foreground dark:text-white">
            Trades simulés récents
          </h2>
          <div className="space-y-2">
            {recentTrades.map((trade) => (
              <div
                key={trade.id}
                className="rounded-xl border border-border bg-muted/20 p-4 dark:border-white/10 dark:bg-white/5">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="font-medium text-foreground dark:text-white">
                      {trade.symbol} - {trade.side}
                    </p>
                    <p className="text-sm text-muted-foreground dark:text-white/70">
                      Quantité: {trade.quantity} | Prix: ${trade.executedPrice}
                    </p>
                  </div>
                  <span className="rounded-full bg-cyan-500/20 px-3 py-1 text-xs text-cyan-800 dark:text-cyan-300">
                    Simulé
                  </span>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminSimulatedTradesPage;
